import { useEffect, useMemo } from 'react'
import { replaceWithRegion } from 'actions/NavigationActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useTypeaheadExtractionRefresh } from './useTypeaheadExtractionRefresh'
import {
  queryKeyAdults,
  queryKeyAmenities,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryKeyChildren,
  queryKeyDestinationId,
  queryKeyExtracting,
  queryKeyFlexibleMonths,
  queryKeyFlexibleNights,
  queryKeyInclusions,
  queryKeyTags,
  queryKeyLandmarkId,
  queryKeyLat,
  queryKeyLng,
  queryKeyPriceGte,
  queryKeyPriceLte,
  queryKeyPropertyId,
  queryKeySortBy,
  queryKeyPages,
  queryKeyQuery,
  queryKeyTypeaheadId,
} from 'constants/url'
import { queryNullChildren, ANYWHERE_PLACE_ID } from 'constants/search'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { mapStreamingDataToExtractedParams } from 'api/mappers/Search/typeaheadStreamingMap'
import { searchQueryParams } from 'lib/search/searchUtils'

/**
 * Maps extracted typeahead streaming data to URL search parameters.
 * Uses mapStreamingDataToExtractedParams for extraction, then applies user preference overrides.
 * @param streamingData The streaming data containing extracted information
 * @param query The natural language query string to use for name fields
 * @param currentSearch The current URL search string (to preserve user-entered values)
 * @returns Array of URL parameters to set
 */
function mapExtractedDataToUrlParams(
  streamingData: App.TypeaheadStreamingData,
  query: string | undefined,
  currentSearch: string,
): Array<{ paramName: string; paramValue: string | number | undefined }> {
  const paramsToSet: Array<{ paramName: string; paramValue: string | number | undefined }> = []
  const currentParams = new URLSearchParams(currentSearch)

  // Use common extraction function
  const extractedParams = mapStreamingDataToExtractedParams(streamingData, query)

  // If no params extracted, set defaults and return
  if (!extractedParams) {
    paramsToSet.push({ paramName: queryKeyDestinationId, paramValue: ANYWHERE_PLACE_ID })
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: '2' })
    paramsToSet.push({ paramName: queryKeyChildren, paramValue: queryNullChildren })
    paramsToSet.push({ paramName: queryKeyExtracting, paramValue: undefined })
    return paramsToSet
  }

  // Location params (no user override for location)
  if (extractedParams.destinationId) {
    paramsToSet.push({ paramName: queryKeyDestinationId, paramValue: extractedParams.destinationId })
  }
  if (extractedParams.landmarkId) {
    paramsToSet.push({ paramName: queryKeyLandmarkId, paramValue: extractedParams.landmarkId })
  }
  if (extractedParams.lat && extractedParams.lng) {
    paramsToSet.push({ paramName: queryKeyLat, paramValue: extractedParams.lat })
    paramsToSet.push({ paramName: queryKeyLng, paramValue: extractedParams.lng })
  }
  if (extractedParams.propertyId) {
    paramsToSet.push({ paramName: queryKeyPropertyId, paramValue: extractedParams.propertyId })
  }
  if (extractedParams.query) {
    paramsToSet.push({ paramName: queryKeyQuery, paramValue: extractedParams.query })
  }

  // Travel dates - user-entered values ALWAYS take precedence over extracted values
  const userCheckIn = currentParams.get(queryKeyCheckIn)
  const userCheckOut = currentParams.get(queryKeyCheckOut)
  const userFlexibleNights = currentParams.get(queryKeyFlexibleNights)
  const userFlexibleMonths = currentParams.get(queryKeyFlexibleMonths)
  const hasUserDates = userCheckIn || userCheckOut || userFlexibleNights || userFlexibleMonths

  if (hasUserDates) {
    if (userCheckIn) paramsToSet.push({ paramName: queryKeyCheckIn, paramValue: userCheckIn })
    if (userCheckOut) paramsToSet.push({ paramName: queryKeyCheckOut, paramValue: userCheckOut })
    if (userFlexibleNights) paramsToSet.push({ paramName: queryKeyFlexibleNights, paramValue: userFlexibleNights })
    if (userFlexibleMonths) paramsToSet.push({ paramName: queryKeyFlexibleMonths, paramValue: userFlexibleMonths })
  } else {
    if (extractedParams.checkIn) paramsToSet.push({ paramName: queryKeyCheckIn, paramValue: extractedParams.checkIn })
    if (extractedParams.checkOut) paramsToSet.push({ paramName: queryKeyCheckOut, paramValue: extractedParams.checkOut })
    if (extractedParams.flexibleNights) paramsToSet.push({ paramName: queryKeyFlexibleNights, paramValue: extractedParams.flexibleNights })
    if (extractedParams.flexibleMonths) paramsToSet.push({ paramName: queryKeyFlexibleMonths, paramValue: extractedParams.flexibleMonths })
  }

  // Occupancy - user-entered values ALWAYS take precedence over extracted values
  const userAdults = currentParams.get(queryKeyAdults)
  const userChildren = currentParams.get(queryKeyChildren)

  if (userAdults) {
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: userAdults })
    if (userChildren) paramsToSet.push({ paramName: queryKeyChildren, paramValue: userChildren })
  } else if (extractedParams.adults) {
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: extractedParams.adults })
    if (extractedParams.children) paramsToSet.push({ paramName: queryKeyChildren, paramValue: extractedParams.children })
  }

  // Tags - user-applied values take precedence
  const userTags = currentParams.get(queryKeyTags)
  if (userTags) {
    paramsToSet.push({ paramName: queryKeyTags, paramValue: userTags })
  } else if (extractedParams.tags && extractedParams.tags.length > 0) {
    paramsToSet.push({ paramName: queryKeyTags, paramValue: extractedParams.tags.join(',') })
  }

  // Inclusions - user-applied values take precedence
  const userInclusions = currentParams.get(queryKeyInclusions)
  if (userInclusions) {
    paramsToSet.push({ paramName: queryKeyInclusions, paramValue: userInclusions })
  } else if (extractedParams.inclusions && extractedParams.inclusions.length > 0) {
    paramsToSet.push({ paramName: queryKeyInclusions, paramValue: extractedParams.inclusions.join(',') })
  }

  // Amenities - user-applied values take precedence
  const userAmenities = currentParams.get(queryKeyAmenities)
  if (userAmenities) {
    paramsToSet.push({ paramName: queryKeyAmenities, paramValue: userAmenities })
  } else if (extractedParams.amenities && extractedParams.amenities.length > 0) {
    paramsToSet.push({ paramName: queryKeyAmenities, paramValue: extractedParams.amenities.join(',') })
  }

  // SortBy - user-applied values take precedence
  const userSortBy = currentParams.get(queryKeySortBy)
  if (userSortBy) {
    paramsToSet.push({ paramName: queryKeySortBy, paramValue: userSortBy })
  } else if (extractedParams.sortBy) {
    paramsToSet.push({ paramName: queryKeySortBy, paramValue: extractedParams.sortBy })
  }

  // Price filters - user-applied values take precedence
  const userPriceGte = currentParams.get(queryKeyPriceGte)
  const userPriceLte = currentParams.get(queryKeyPriceLte)
  if (userPriceGte || userPriceLte) {
    if (userPriceGte) paramsToSet.push({ paramName: queryKeyPriceGte, paramValue: userPriceGte })
    if (userPriceLte) paramsToSet.push({ paramName: queryKeyPriceLte, paramValue: userPriceLte })
  } else {
    if (extractedParams.priceGte) paramsToSet.push({ paramName: queryKeyPriceGte, paramValue: extractedParams.priceGte })
    if (extractedParams.priceLte) paramsToSet.push({ paramName: queryKeyPriceLte, paramValue: extractedParams.priceLte })
  }

  // Remove extracting=true param
  paramsToSet.push({ paramName: queryKeyExtracting, paramValue: undefined })

  return paramsToSet
}

interface UseTypeaheadExtractionUrlUpdateParams {
  extracting: string | undefined
  typeaheadId: string | undefined
  query: string | undefined
  windowSearch: string
  pathname: string
  typeaheadTypes?: Array<App.SearchPlaceType>
}

interface UseTypeaheadExtractionUrlUpdateReturn {
  isExtracting: boolean
  isExtractionComplete: boolean
  isSearchBlocked: boolean
}

/**
 * Hook to handle typeahead extraction URL updates
 * - Checks if extraction is in progress
 * - Waits for extraction to complete
 * - Maps extracted data to URL params
 * - Updates URL and removes extracting=true when complete
 * - Blocks search while extraction is in progress
 */
export function useTypeaheadExtractionUrlUpdate({
  extracting,
  typeaheadId,
  query,
  windowSearch,
  pathname,
  typeaheadTypes,
}: UseTypeaheadExtractionUrlUpdateParams): UseTypeaheadExtractionUrlUpdateReturn {
  const dispatch = useAppDispatch()
  const streamingData = useAppSelector(state =>
    typeaheadId ? state.search.typeaheadStreamingData[typeaheadId] : undefined
  )

  const isExtracting = extracting === 'true'

  // Handle page refresh during extraction - re-dispatch streamTypeahead if needed
  useTypeaheadExtractionRefresh({
    isExtracting,
    typeaheadId,
    query: query,
    typeaheadTypes,
  })

  // Check if extraction is complete
  const isExtractionComplete = useMemo(() => {
    if (!isExtracting || !streamingData || !query) {
      return false
    }
    return streamingData.isComplete && streamingData.completedInput === query
  }, [isExtracting, streamingData, query])

  // Update URL with extracted data when extraction completes
  useEffect(() => {
    if (!isExtractionComplete || !streamingData || !typeaheadId) {
      return
    }

    // Map extracted data to URL params (pass current search to preserve user-entered values)
    const paramsToSet = mapExtractedDataToUrlParams(streamingData, query, windowSearch)

    // Preserve the current typeaheadId - it should stay in URL after extraction completes
    paramsToSet.push({ paramName: queryKeyTypeaheadId, paramValue: typeaheadId })

    // Clear all search params first (like a new search would)
    const clearedSearch = deleteSearchParams(windowSearch, ...searchQueryParams, queryKeyPages)

    // Set only the extracted params (including the preserved typeaheadId)
    const newSearch = setManySearchParamValues(clearedSearch, paramsToSet)

    dispatch(replaceWithRegion(pathname, newSearch))
  }, [isExtractionComplete, streamingData, typeaheadId, windowSearch, pathname, dispatch, query])

  // Block search while extraction is in progress
  const isSearchBlocked = isExtracting && !isExtractionComplete

  return {
    isExtracting,
    isExtractionComplete,
    isSearchBlocked,
  }
}
